import { createServerFn } from "@tanstack/react-start";

import { prisma } from "@/server-fns/prisma";
import { getStripeClient } from "@/server-fns/stripe";

type CreateBookingSetupIntentInput = {
    storeId: string;
    email: string;
};

// Guest-facing / unauthenticated — same trust model as saveBookingByGuestFn.
// No store-ownership check: this only creates a Stripe SetupIntent, no DB writes and no charge.
// Turnstile verification is intentionally deferred to the saveBookingByGuestFn call that follows
// right after (see PaymentStep), which is the call that actually persists a booking — a Turnstile
// token is single-use, so consuming it here would make that later verification fail.
//
// Two guards keep this from being a free Stripe-object faucet:
//  1. the store must actually exist AND have the cancellation-fee policy switched on with an
//     ACTIVE Connect account — i.e. this is only reachable for stores whose booking flow really
//     does have a payment step;
//  2. Stripe Customers are reused per (email, storeId) instead of minted on every call, so
//     PaymentStep → Back → forward, a retry after a declined card, or a 3DS re-entry all land on
//     the same Customer rather than leaving orphans behind.
export const createBookingSetupIntentFn = createServerFn({ method: "POST" })
    .inputValidator((data: CreateBookingSetupIntentInput) => data)
    .handler(async ({ data }) => {
        const store = await prisma.store.findUnique({
            where: { id: data.storeId },
            select: {
                stripeConnectStatus: true,
                bookingSettings: { select: { cancellationFeeEnabled: true } },
            },
        });

        if (
            !store ||
            store.stripeConnectStatus !== "ACTIVE" ||
            !store.bookingSettings?.cancellationFeeEnabled
        ) {
            throw new Error("This store does not collect a cancellation fee");
        }

        const stripe = getStripeClient();

        // customers.list(email) is immediately consistent (unlike customers.search, which lags
        // by up to a minute) — important here, because the retry we most need to dedupe happens
        // seconds after the first call.
        const existing = await stripe.customers.list({ email: data.email, limit: 20 });
        const reusable = existing.data.find(
            (candidate) => !candidate.deleted && candidate.metadata?.storeId === data.storeId,
        );

        const customer =
            reusable ??
            (await stripe.customers.create({
                email: data.email,
                metadata: { storeId: data.storeId },
            }));

        const setupIntent = await stripe.setupIntents.create({
            customer: customer.id,
            payment_method_types: ["card"],
            usage: "off_session",
        });

        if (!setupIntent.client_secret) {
            throw new Error("Stripe did not return a client secret for the SetupIntent");
        }

        return {
            clientSecret: setupIntent.client_secret,
            stripeCustomerId: customer.id,
            stripeSetupIntentId: setupIntent.id,
        };
    });
